import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    MenuItem, Box, Autocomplete, CircularProgress,
} from '@mui/material';
import { ITask, ITaskRequest, TaskPriority } from '../../interfaces/task.interface';
import { TaskService } from '../../services/task.service';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';

interface Props {
    open: boolean;
    task?: ITask | null;
    onClose: () => void;
    onSaved: () => void;
}

interface UserOption {
    id: string;
    username: string;
}

const toLocalInput = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const TaskForm: React.FC<Props> = ({ open, task, onClose, onSaved }) => {
    const toast = useToast();
    const { t } = useTranslation(['task', 'common']);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [assignee, setAssignee] = useState<UserOption | null>(null);
    const [assigneeInput, setAssigneeInput] = useState('');
    const [assigneeOptions, setAssigneeOptions] = useState<UserOption[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (task) {
            setTitle(task.title);
            setDescription(task.description || '');
            setAssignee({ id: task.assigneeUserId, username: task.assigneeName || '' });
            setDueDate(toLocalInput(task.dueDate));
            setPriority(task.priority);
        } else {
            setTitle('');
            setDescription('');
            setAssignee(null);
            setDueDate('');
            setPriority('MEDIUM');
        }
        setAssigneeInput('');
        setAssigneeOptions([]);
    }, [task, open]);

    useEffect(() => {
        if (!open || !assigneeInput.trim()) {
            setAssigneeOptions([]);
            return;
        }
        const handle = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const res = await UserService.getInstitutionUsers(0, 20, undefined, assigneeInput);
                setAssigneeOptions(res.content);
            } catch (err) {
                toast.showApiError(err, t('taskForm.errors.searchUsers'));
            } finally {
                setSearchLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assigneeInput, open]);

    const handleSubmit = async () => {
        if (!title.trim()) {
            toast.showError(t('taskForm.errors.titleRequired'));
            return;
        }
        if (!assignee) {
            toast.showError(t('taskForm.errors.assigneeRequired'));
            return;
        }
        const request: ITaskRequest = {
            title: title.trim(),
            description: description || null,
            assigneeUserId: assignee.id,
            dueDate: dueDate ? new Date(dueDate).toISOString() : null,
            priority,
        };
        setSaving(true);
        try {
            if (task) {
                await TaskService.update(task.id, institutionId, request);
            } else {
                await TaskService.create(institutionId, request);
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, t('taskForm.errors.save'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{task ? t('taskForm.editTitle') : t('taskForm.newTitle')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label={t('taskForm.titleLabel')} value={title} onChange={e => setTitle(e.target.value)} fullWidth required inputProps={{ maxLength: 200 }} />
                    <TextField label={t('taskForm.descriptionLabel')} value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline minRows={3} inputProps={{ maxLength: 10000 }} />
                    <Autocomplete
                        value={assignee}
                        onChange={(_, v) => setAssignee(v)}
                        onInputChange={(_, v) => setAssigneeInput(v)}
                        options={assigneeOptions}
                        getOptionLabel={(o) => displayUsername(o.username)}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        loading={searchLoading}
                        renderInput={(params) => (
                            <TextField {...params} label={t('taskForm.assigneeLabel')} required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (<>{searchLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                                }}
                            />
                        )}
                    />
                    <TextField label={t('taskForm.dueDateLabel')} type="datetime-local" value={dueDate} onChange={e => setDueDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                    <TextField select label={t('taskForm.priorityLabel')} value={priority} onChange={e => setPriority(e.target.value as TaskPriority)} fullWidth>
                        <MenuItem value="LOW">{t('taskForm.priority.LOW')}</MenuItem>
                        <MenuItem value="MEDIUM">{t('taskForm.priority.MEDIUM')}</MenuItem>
                        <MenuItem value="HIGH">{t('taskForm.priority.HIGH')}</MenuItem>
                    </TextField>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving}>
                    {task ? t('common:actions.save') : t('taskForm.assign')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskForm;
